import React from 'react'
import { redirect } from 'redux-first-router'
import { useDispatch, useSelector } from 'react-redux'

import { TAB_NAMES } from 'components/pages/Jobs/model'
import { DEFAULT_JOBS_QUERY_PARAMS } from 'constants/projects'
import { getUser } from 'ducks/auth'
import { toProjects, useLocation } from 'ducks/router'
import { useSnackbar } from 'hooks'
import { useAssessmentFormState } from './useAssessmentFormState'
import { useData } from './useData'
import { useEvaluator } from './useEvaluator'
import { useMutations } from './useMutations'
import { useQueries } from './useQueries'
import { useStepsState } from './useStepsState'
import { useConfirmSubmitModal } from './useConfirmSubmitModal'
import { useEditMode } from './useEditMode'
import { STATIC_PAGES } from '../model'

// NOTE: hook that groups up all the local hooks and
// exposes state and handlers for the interview components
export const useInternalState = () => {
  const location = useLocation()
  const user = useSelector(getUser)
  const dispatch = useDispatch()

  const {
    payload: { uuid },
  } = location

  const { handleFetchInterview } = useQueries()

  const { handleDeleteEvaluator, handlePatchEvaluation, handlePostEvaluator } =
    useMutations()

  const {
    state: { meta, response, isFetching },
    handlers: { handleFetch, handleSetResponse },
  } = useData({ uuid }, { handleFetchInterview })

  const {
    candidate = {},
    current_status,
    ebber_scores,
    evaluation = {},
    evaluators = [],
    id,
    interview_summary,
    sections = [],
    stats,
  } = response ?? {}

  const { project } = candidate

  const {
    handlers: { handleShowSnackbar },
  } = useSnackbar()

  const {
    state: {
      activeSection,
      activeStep,
      steps,
      isCategoryStep,
      isIntroductionStep,
      isNextStepDisabled,
      isPreviousStepDisabled,
      isReadOnly,
      isReportStep,
      isSummaryStep,
      isStaticStep,
    },
    handlers: {
      handleChangeNextStep,
      handleChangePreviousStep,
      handleChangeStep,
    },
  } = useStepsState({ current_status, evaluation, sections })

  const {
    state: { isEditMode },
    handlers: { toggleEditMode },
  } = useEditMode({ handleChangeStep })

  const {
    state: { assessment, hasEmptyScore, questionComments },
    handlers: { handleFormSubmit, handleQuestionCommentChange, watch },
  } = useAssessmentFormState(
    { evaluation },
    { handleChangeStep, handleFetch, handlePatchEvaluation, handleSetResponse },
  )

  const {
    state: { evaluator, hasEvaluators, isDeleteEvaluatorOpen, isSelfAdded },
    handlers: {
      handleDeleteEvaluatorClose,
      handleDeleteEvaluatorOpen,
      handleDeleteEvaluatorConfirm,
      handleAddSelf,
    },
  } = useEvaluator(
    { evaluation, evaluators, id, user },
    {
      handleDeleteEvaluator,
      handleFetch,
      handlePostEvaluator,
      handleSetResponse,
      handleShowSnackbar,
    },
  )

  const {
    handleConfirmSubmitClose,
    handleConfirmSubmitOpen,
    isConfirmSubmitOpen,
  } = useConfirmSubmitModal()

  const closeModalAndValidate = () => {
    handleFormSubmit()
    handleConfirmSubmitClose()

    if (isEditMode) {
      toggleEditMode()
    }
  }

  const questionsCount = sections
    .filter(({ key }) => !STATIC_PAGES.includes(key))
    .reduce((acc, section) => acc + (section.questions?.length as number), 0)

  const interviewAverageScore = Math.round(
    (interview_summary?.interview_score as number) * 100,
  )

  const testAverageScore = Math.round((stats?.average?.tests as number) * 100)

  const hasNoSections = !isFetching && sections.length === 0
  const has404Error =
    (meta as { HTTPstatus?: number } | null)?.HTTPstatus === 404

  const handleRedirectToJobs = React.useCallback(() => {
    dispatch(redirect(toProjects(null, null, DEFAULT_JOBS_QUERY_PARAMS)))
  }, [dispatch])

  const handleInterviewClose = React.useCallback(() => {
    current_status === 5
      ? dispatch(toProjects(project, TAB_NAMES.RESULTS))
      : dispatch(toProjects(project, TAB_NAMES.INTERVIEWS))
  }, [current_status, project, dispatch])

  const shouldRedirectToJobs = hasNoSections || has404Error

  // A dispatch during render updates the subscribed components mid-render,
  // which React rejects, so the redirect waits for the commit
  React.useEffect(() => {
    if (shouldRedirectToJobs) handleRedirectToJobs()
  }, [shouldRedirectToJobs, handleRedirectToJobs])

  return {
    state: {
      activeSection,
      activeStep,
      assessment: { ...assessment, watch },
      questionComments,
      isValid: assessment?.formState?.isValid,
      interviewAverageScore,
      candidate,
      current_status,
      ebberScores: ebber_scores,
      evaluator,
      evaluation,
      evaluators,
      id,
      interviewSummary: interview_summary,
      questionsCount,
      sections,
      steps,
      testAverageScore,
      user,
      hasEmptyScore,
      hasEvaluators,
      isCategoryStep,
      isDeleteEvaluatorOpen,
      isFetching,
      isIntroductionStep,
      isNextStepDisabled,
      isPreviousStepDisabled,
      isReadOnly,
      isReportStep,
      isSelfAdded,
      isSummaryStep,
      isStaticStep,
      isConfirmSubmitOpen,
      isEditMode,
    },
    handlers: {
      handleChangeNextStep,
      handleChangePreviousStep,
      handleChangeStep,
      handleFormSubmit,
      handleQuestionCommentChange,
      handleInterviewClose,
      handleDeleteEvaluatorClose,
      handleDeleteEvaluatorOpen,
      handleDeleteEvaluatorConfirm,
      handleAddSelf,
      handleShowSnackbar,
      handleConfirmSubmitClose,
      handleConfirmSubmitOpen,
      closeModalAndValidate,
      toggleEditMode,
    },
  }
}
